import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { PagesModule } from '@gravity-angular/pages';
import { ComponentsModule } from '@gravity-angular/components';
import { BaseModule } from '@gravity-angular/base';
import { MatIconModule } from '@angular/material/icon';
import { AlarmsService } from '../../core/services/alarms.service';
import { DashboardDataService } from '../../core/services/dashboard-data.service';
import { Alarm } from '../../core/models/dashboard-data.model';

@Component({
  selector: 'app-alarms',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    PagesModule,
    ComponentsModule,
    BaseModule,
    MatIconModule,
  ],
  templateUrl: './alarms.html',
  styleUrl: './alarms.scss',
})
export class Alarms {
  private alarmsService = inject(AlarmsService);
  protected dashboardData = inject(DashboardDataService);

  alarms = this.dashboardData.alarms;
  error = signal<string | null>(null);
  activeFilter = signal<'all' | 'ACTIVE' | 'CEASED' | 'ACKNOWLEDGED'>('ACTIVE');
  actionInProgress = signal<string | null>(null);

  filteredAlarms = computed(() => {
    const f = this.activeFilter();
    return f === 'all' ? this.alarms() : this.alarms().filter(a => a.state === f);
  });

  activeCounts = computed(() => ({
    all: this.alarms().length,
    active: this.alarms().filter(a => a.state === 'ACTIVE').length,
    ceased: this.alarms().filter(a => a.state === 'CEASED').length,
    acknowledged: this.alarms().filter(a => a.state === 'ACKNOWLEDGED').length,
  }));

  setFilter(filter: string): void {
    this.activeFilter.set(filter as 'all' | 'ACTIVE' | 'CEASED' | 'ACKNOWLEDGED');
  }

  acknowledgeAlarm(alarm: Alarm): void {
    this.actionInProgress.set(alarm.id);
    this.alarmsService.acknowledgeAlarm(alarm.id).subscribe({
      next: () => { this.actionInProgress.set(null); },
      error: (err) => {
        console.error('Acknowledge failed:', err);
        this.actionInProgress.set(null);
      }
    });
  }

  resetAlarm(alarm: Alarm): void {
    this.actionInProgress.set(alarm.id);
    this.alarmsService.resetAlarm(alarm.id).subscribe({
      next: () => { this.actionInProgress.set(null); },
      error: (err) => {
        console.error('Reset failed:', err);
        this.actionInProgress.set(null);
      }
    });
  }

  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'HIGH': return 'priority-high';
      case 'MEDIUM': return 'priority-medium';
      case 'LOW': return 'priority-low';
      default: return 'priority-low';
    }
  }

  getStateClass(state: string): string {
    switch (state) {
      case 'ACTIVE': return 'state-active';
      case 'CEASED': return 'state-ceased';
      case 'ACKNOWLEDGED': return 'state-acknowledged';
      default: return '';
    }
  }
}
